import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { centsToInputDollars, formatCurrency } from '@/lib/formatters';
import type { Item, Location } from '@/features/inventory/hooks';
import type {
  CreateVendorInput,
  OrderMethod,
  PoStatus,
  PurchaseCard,
  PurchaseMethod,
  Vendor,
} from './hooks';
import { findVendorNameMatches } from './vendorNames';

// -----------------------------------------------------------------------
// "Import order PDF" on the New PO dialog. The `po-document-extract`
// edge function reads the document; this module turns its reply into
// form values plus a record of which values came from the document (and
// how sure the read was), so the dialog can show the person what was
// pre-filled and what still needs them before they confirm.
// -----------------------------------------------------------------------

export type ExtractConfidence = 'high' | 'medium' | 'low';

export interface ExtractedField<T> {
  value: T;
  confidence: ExtractConfidence;
  evidence: string | null;
}

export interface ExtractedVendor {
  vendor_id: string | null;
  name: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  confidence: ExtractConfidence;
  evidence: string | null;
}

export interface ExtractedLine {
  description: string;
  vendor_sku: string | null;
  quantity: number | null;
  uom: string | null;
  unit_price_cents: number | null;
  line_total_cents: number | null;
  item_id: string | null;
  item_match_confidence: ExtractConfidence | null;
  confidence: ExtractConfidence;
  evidence: string | null;
}

/** Mirrors `PoExtraction` in supabase/functions/po-document-extract. */
export interface PoExtraction {
  document_type: 'order_confirmation' | 'receipt' | 'invoice' | 'quote' | 'other';
  summary: string | null;
  currency: string | null;
  vendor: ExtractedVendor | null;
  order_platform: ExtractedField<string> | null;
  order_method: ExtractedField<OrderMethod> | null;
  order_reference: ExtractedField<string> | null;
  ordered_at: ExtractedField<string> | null;
  expected_at: ExtractedField<string> | null;
  purchase_method: ExtractedField<Exclude<PurchaseMethod, 'amex' | 'mercury_card'>> | null;
  card_last_four: ExtractedField<string> | null;
  purchase_card_id: string | null;
  payment_terms: ExtractedField<string> | null;
  ship_to_location_id: ExtractedField<string> | null;
  ship_to_address: string | null;
  tax_cents: ExtractedField<number> | null;
  shipping_cents: ExtractedField<number> | null;
  shipping_description: string | null;
  fees_cents: ExtractedField<number> | null;
  discount_cents: ExtractedField<number> | null;
  total_cents: ExtractedField<number> | null;
  carrier: ExtractedField<string> | null;
  tracking_number: ExtractedField<string> | null;
  shipped_at: ExtractedField<string> | null;
  lines: ExtractedLine[];
  notes: string | null;
}

export const PO_IMPORT_MAX_BYTES = 15 * 1024 * 1024;
export const PO_IMPORT_ACCEPT = 'application/pdf,.pdf,image/png,image/jpeg,image/webp';

export function isImportableOrderFile(file: File): boolean {
  return (
    file.type === 'application/pdf' ||
    /\.pdf$/i.test(file.name) ||
    ['image/png', 'image/jpeg', 'image/webp'].includes(file.type)
  );
}

async function fileToBase64(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    binary += String.fromCharCode(...buf.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function useExtractPoDocument() {
  return useMutation({
    mutationFn: async (file: File) => {
      if (!isImportableOrderFile(file)) throw new Error('Upload the order as a PDF.');
      if (file.size > PO_IMPORT_MAX_BYTES) throw new Error('That file is too large (15 MB max).');
      const mediaType = file.type || (/\.pdf$/i.test(file.name) ? 'application/pdf' : '');
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        extraction?: PoExtraction;
        error?: string;
      }>('po-document-extract', {
        body: {
          file_base64: await fileToBase64(file),
          media_type: mediaType,
          file_name: file.name,
        },
      });
      if (error) {
        const ctx = (error as { context?: Response }).context;
        if (ctx) {
          const body = (await ctx.json().catch(() => null)) as { error?: string } | null;
          if (body?.error) throw new Error(body.error);
        }
        throw error;
      }
      if (!data?.ok || !data.extraction) {
        throw new Error(data?.error ?? 'Could not read that document.');
      }
      return data.extraction;
    },
  });
}

// -----------------------------------------------------------------------
// Draft
// -----------------------------------------------------------------------

/** Where a form value came from on the document. */
export interface Prefill {
  confidence: ExtractConfidence;
  evidence: string | null;
}

export type PoImportField =
  | 'vendor'
  | 'status'
  | 'purchaseMethod'
  | 'paymentTerms'
  | 'shipTo'
  | 'orderPlatform'
  | 'orderReference'
  | 'orderedAt'
  | 'expectedAt'
  | 'tax'
  | 'processingFee'
  | 'carrier'
  | 'trackingNumber'
  | 'shippedAt'
  | 'notes';

export type PoImportLineField = 'item' | 'quantity' | 'unitCost' | 'orderMethod';

/** Prefill map key for a line field. */
export function lineFieldKey(lineKey: string, field: PoImportLineField): string {
  return `line:${lineKey}:${field}`;
}

/** Prefill map key for a shipping charge. */
export function chargeFieldKey(chargeId: string): string {
  return `charge:${chargeId}`;
}

export interface PoImportLine {
  itemId: string;
  sku: string;
  name: string;
  uom: string;
  /** Vendor SKU printed on the document, if any. */
  supplierPartNumber: string;
  quantity: string;
  unitCost: string;
  orderMethod: OrderMethod | '';
  prefill: Partial<Record<PoImportLineField, Prefill>>;
  /** Why this line still needs a look, keyed by field. */
  hints: Partial<Record<PoImportLineField, string>>;
}

export interface PoImportCharge {
  description: string;
  reference: string;
  amount: string;
  chargedAt: string;
  prefill: Prefill;
}

export interface PoImportDraft {
  vendorId: string | null;
  status: PoStatus;
  purchaseMethod: PurchaseMethod | '';
  purchaseCardId: string;
  paymentTerms: string;
  shipToId: string | null;
  orderPlatform: string;
  orderReference: string;
  orderedAt: string;
  expectedAt: string;
  tax: string;
  processingFee: string;
  carrier: string;
  trackingNumber: string;
  shippedAt: string;
  notes: string;
  lines: PoImportLine[];
  charge: PoImportCharge | null;
  prefill: Partial<Record<PoImportField, Prefill>>;
  /** Why a field still needs a look even though (or because) nothing was filled. */
  hints: Partial<Record<PoImportField, string>>;
  /** Pre-fills the "create vendor" form when the seller isn't in the directory yet. */
  newVendor: CreateVendorInput | null;
  documentTotalCents: number | null;
  summary: string | null;
}

export interface PoImportContext {
  vendors: Vendor[];
  items: Item[];
  locations: Location[];
  cards: PurchaseCard[];
}

const fromField = (f: ExtractedField<unknown>): Prefill => ({
  confidence: f.confidence,
  evidence: f.evidence,
});

const lower = (c: ExtractConfidence, to: ExtractConfidence): ExtractConfidence =>
  c === 'low' || to === 'low' ? 'low' : c === 'medium' || to === 'medium' ? 'medium' : 'high';

function inferOrderMethod(x: PoExtraction): { method: OrderMethod; prefill: Prefill } {
  if (x.order_method) return { method: x.order_method.value, prefill: fromField(x.order_method) };
  const platform = x.order_platform?.value ?? '';
  if (/amazon/i.test(platform)) {
    return { method: 'amazon', prefill: { confidence: 'medium', evidence: platform } };
  }
  return { method: 'website', prefill: { confidence: 'low', evidence: null } };
}

/**
 * Map an extraction onto New PO form values. Pure: everything it needs
 * from the org (vendors, catalog, locations, cards) is passed in, and
 * ids the edge function returned are re-checked against those lists.
 */
export function buildPoImportDraft(x: PoExtraction, ctx: PoImportContext): PoImportDraft {
  const prefill: PoImportDraft['prefill'] = {};
  const hints: PoImportDraft['hints'] = {};
  const set = <K extends PoImportField>(key: K, f: ExtractedField<unknown> | null) => {
    if (f) prefill[key] = fromField(f);
  };

  // ----- Vendor -----
  let vendorId: string | null = null;
  let newVendor: CreateVendorInput | null = null;
  const v = x.vendor;
  if (v) {
    const byId = v.vendor_id ? ctx.vendors.find((row) => row.id === v.vendor_id) : undefined;
    const byName = !byId && v.name ? findVendorNameMatches(v.name, ctx.vendors).exact : null;
    const match = byId ?? byName ?? null;
    if (match) {
      vendorId = match.id;
      prefill.vendor = {
        confidence: byId ? v.confidence : lower(v.confidence, 'medium'),
        evidence: v.evidence ?? v.name,
      };
    } else if (v.name) {
      hints.vendor = `“${v.name}” isn’t in the vendor directory yet — create it or pick the right vendor.`;
      newVendor = {
        name: v.name,
        website: v.website,
        email: v.email,
        phone: v.phone,
        address_line1: v.address_line1,
        address_line2: v.address_line2,
        city: v.city,
        state: v.state,
        zip_code: v.zip_code,
      };
    }
  }
  if (!vendorId && !hints.vendor) hints.vendor = 'No seller found on the document.';

  // ----- Status -----
  let status: PoStatus = 'ordered';
  let statusEvidence = 'The document is an order confirmation / receipt.';
  if (x.document_type === 'quote') {
    status = 'internal_po';
    statusEvidence = 'The document is a quote, not a placed order.';
  } else if (x.tracking_number || x.shipped_at) {
    status = 'shipped';
    statusEvidence = 'The document shows a shipment.';
  }
  prefill.status = { confidence: 'medium', evidence: statusEvidence };

  // ----- Purchase method / card -----
  let purchaseMethod: PurchaseMethod | '' = x.purchase_method?.value ?? '';
  let purchaseCardId = '';
  if (x.purchase_method) set('purchaseMethod', x.purchase_method);
  if (!purchaseMethod && x.card_last_four) purchaseMethod = 'card';
  if (purchaseMethod === 'card') {
    const card = x.purchase_card_id ? ctx.cards.find((c) => c.id === x.purchase_card_id) : null;
    if (card) {
      purchaseCardId = card.id;
      const src = x.card_last_four ?? x.purchase_method;
      prefill.purchaseMethod = src
        ? fromField(src)
        : { confidence: 'medium', evidence: `Card ending ${card.last_four}` };
    } else {
      delete prefill.purchaseMethod;
      hints.purchaseMethod = x.card_last_four
        ? `Paid with a card ending ${x.card_last_four.value}, which isn’t on Card mapping — pick the card used.`
        : 'Paid by card, but the document doesn’t say which — pick the card used.';
    }
  }
  if (!purchaseMethod) hints.purchaseMethod = 'The document doesn’t say how this was paid.';

  const paymentTerms = purchaseMethod === 'terms' ? (x.payment_terms?.value ?? '') : '';
  if (paymentTerms) set('paymentTerms', x.payment_terms);

  // ----- Ship to -----
  let shipToId: string | null = null;
  const loc = x.ship_to_location_id
    ? ctx.locations.find((l) => l.id === x.ship_to_location_id!.value)
    : null;
  if (loc) {
    shipToId = loc.id;
    prefill.shipTo = {
      confidence: x.ship_to_location_id!.confidence,
      evidence: x.ship_to_address ?? x.ship_to_location_id!.evidence,
    };
  } else if (x.ship_to_address) {
    hints.shipTo = `Ships to “${x.ship_to_address}”, which doesn’t match a location — check the ship-to.`;
  }

  // ----- Order placed / dates -----
  const orderPlatform = x.order_platform?.value ?? '';
  set('orderPlatform', x.order_platform);
  const orderReference = x.order_reference?.value ?? '';
  set('orderReference', x.order_reference);
  const orderedAt = x.ordered_at?.value ?? '';
  set('orderedAt', x.ordered_at);
  if (!orderedAt) hints.orderedAt = 'No order date found on the document.';
  const expectedAt = x.expected_at?.value ?? '';
  set('expectedAt', x.expected_at);

  // ----- Money -----
  const tax = x.tax_cents ? centsToInputDollars(x.tax_cents.value) : '';
  set('tax', x.tax_cents);
  const processingFee = x.fees_cents ? centsToInputDollars(x.fees_cents.value) : '';
  set('processingFee', x.fees_cents);

  // ----- Tracking -----
  const carrier = x.carrier?.value ?? '';
  set('carrier', x.carrier);
  const trackingNumber = x.tracking_number?.value ?? '';
  set('trackingNumber', x.tracking_number);
  const shippedAt = x.shipped_at?.value ?? '';
  set('shippedAt', x.shipped_at);

  // ----- Lines -----
  const orderMethod = inferOrderMethod(x);
  const itemsById = new Map(ctx.items.map((it) => [it.id, it]));
  const lines: PoImportLine[] = x.lines.map((l) => {
    const linePrefill: PoImportLine['prefill'] = {};
    const lineHints: PoImportLine['hints'] = {};
    const evidence = l.evidence ?? l.description;
    const item = l.item_id ? itemsById.get(l.item_id) : undefined;
    const usable = item && item.is_active && item.status !== 'archived';
    let quantity = '';
    if (l.quantity != null) {
      if (Number.isInteger(l.quantity)) {
        quantity = String(l.quantity);
        linePrefill.quantity = { confidence: l.confidence, evidence };
      } else {
        lineHints.quantity = `The document says ${l.quantity} — POs take whole units.`;
      }
    } else {
      lineHints.quantity = 'No quantity found on the document.';
    }
    const unitCost = l.unit_price_cents != null ? centsToInputDollars(l.unit_price_cents) : '';
    if (unitCost) linePrefill.unitCost = { confidence: l.confidence, evidence };
    else lineHints.unitCost = 'No price found on the document.';
    linePrefill.orderMethod = orderMethod.prefill;

    if (usable) {
      linePrefill.item = {
        confidence: l.item_match_confidence ?? 'medium',
        evidence: [l.description, l.vendor_sku].filter(Boolean).join(' · ') || null,
      };
      return {
        itemId: item.id,
        sku: item.sku,
        name: item.name,
        uom: item.base_uom,
        supplierPartNumber: l.vendor_sku?.trim() ?? '',
        quantity,
        unitCost,
        orderMethod: orderMethod.method,
        prefill: linePrefill,
        hints: lineHints,
      };
    }
    const name = [l.description, l.vendor_sku ? `(${l.vendor_sku})` : null]
      .filter(Boolean)
      .join(' ')
      .slice(0, 400);
    linePrefill.item = { confidence: l.confidence, evidence };
    lineHints.item = 'Not matched to a catalog item — kept as a custom item.';
    return {
      itemId: '',
      sku: '',
      name,
      uom: l.uom || 'each',
      supplierPartNumber: l.vendor_sku?.trim() ?? '',
      quantity,
      unitCost,
      orderMethod: orderMethod.method,
      prefill: linePrefill,
      hints: lineHints,
    };
  });

  // ----- Shipping charge -----
  let charge: PoImportCharge | null = null;
  if (x.shipping_cents && x.shipping_cents.value > 0) {
    const who = orderPlatform || x.vendor?.name || '';
    charge = {
      description: x.shipping_description ?? (who ? `${who} shipping` : 'Shipping').slice(0, 200),
      reference: trackingNumber,
      amount: centsToInputDollars(x.shipping_cents.value),
      chargedAt: orderedAt,
      prefill: fromField(x.shipping_cents),
    };
  }

  // ----- Notes -----
  const noteParts: string[] = [];
  if (x.notes) noteParts.push(x.notes);
  if (x.discount_cents && x.discount_cents.value > 0) {
    noteParts.push(`Order discount on the document: ${formatCurrency(x.discount_cents.value)}.`);
  }
  if (x.currency && x.currency !== 'USD') {
    noteParts.push(`Document amounts are in ${x.currency}.`);
    hints.tax = `Amounts on the document are in ${x.currency} — convert to USD if needed.`;
  }
  const notes = noteParts.join(' ').slice(0, 2000);
  if (notes) prefill.notes = { confidence: 'medium', evidence: null };

  return {
    vendorId,
    status,
    purchaseMethod,
    purchaseCardId,
    paymentTerms,
    shipToId,
    orderPlatform,
    orderReference,
    orderedAt,
    expectedAt,
    tax,
    processingFee,
    carrier,
    trackingNumber,
    shippedAt,
    notes,
    lines,
    charge,
    prefill,
    hints,
    newVendor,
    documentTotalCents: x.total_cents?.value ?? null,
    summary: x.summary,
  };
}
